// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import loadQuotes from "@mcmc/App/Handlers/Quotes/GetQuotes";
import { CustomQuoteModel } from "@mcmc/App/Services/QuoteService";
import { recentOrdersStyles } from "@mcmc/Overrides/Widgets/Common/RecentOrders";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        language: state.context.session.language,
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface QuoteViewModel extends CustomQuoteModel {
    id: string;
}

interface State {
    quotes?: QuoteViewModel[];
    isLoaded: boolean;
}

export interface RecentQuotesStyles {
    cardList?: GridContainerProps;
    cardListGrid?: GridItemProps;
    tableGridItem?: GridItemProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    cardList: recentOrdersStyles.cardList,
    cardListGrid: recentOrdersStyles.cardListGrid,
    tableGridItem: recentOrdersStyles.tableGridItem,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { quotes: undefined, isLoaded: false };
    }

    componentDidMount() {
        if (this.props.canViewQuotes && !this.state.isLoaded) {
            this.props.loadQuotes({
                page: 1,
                pageSize: this.props.fields.numberOfRecords,
                onSuccess: result => {
                    const language = this.props.language;
                    this.setState({
                        quotes: result.quotes?.map((quote, index) => ({
                            ...quote,
                            id: index + quote.quoteNumber,
                            quoteNumber: quote.quoteNumber,
                            purchaseOrder: quote.purchaseOrder,
                            status: quote.status,
                            requestedDate: quote.requestedDate
                                ? getLocalizedDateTime({ dateTime: new Date(quote.requestedDate), language })
                                : "",
                        })),
                        isLoaded: true,
                    });
                },
                onError: () => {
                    this.setState({
                        quotes: undefined,
                        isLoaded: true,
                    });
                },
            });
        }
    }

    render() {
        const { myQuotesPageNavLink, canViewQuotes } = this.props;
        const quotes = this.state.quotes;
        if (!canViewQuotes || !quotes || quotes.length === 0) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        return (
            <CardList {...styles.cardList}>
                <GridItem {...styles.cardListGrid}>
                    <CardListHeading heading={this.props.fields.title} viewAllUrl={myQuotesPageUrl} />
                </GridItem>
                <GridItem {...styles.tableGridItem}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader>{translate("PO # / Ref #")}</DataTableHeader>
                            <DataTableHeader>{translate("Quote #")}</DataTableHeader>
                            <DataTableHeader>{translate("Status")}</DataTableHeader>
                            <DataTableHeader>{translate("Requested Date")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {quotes.map(({ id, purchaseOrder, quoteNumber, status, requestedDate }) => (
                                <DataTableRow key={id}>
                                    <DataTableCell>
                                        <Link href={`${this.props.rfqQuoteDetailsPageUrl}?quoteId=${quoteNumber}`}>
                                            {purchaseOrder}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell>
                                        <Link href={`${this.props.rfqQuoteDetailsPageUrl}?quoteId=${quoteNumber}`}>
                                            {quoteNumber}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell>{status}</DataTableCell>
                                    <DataTableCell>{requestedDate}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </GridItem>
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: translate("Recent Quotes"),
                isRequired: true,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
